import { useParams, Link } from "react-router-dom";
import { useState, useMemo } from "react";
import SEOHead from "@/components/feature/SEOHead";
import { WebPageSchema } from "@/components/feature/SchemaOrg";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/base/ArticleCard";
import { articles, categories } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || "all";
  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];

  const filteredArticles = useMemo(() => {
    if (activeSlug === "all") return articles;
    return articles.filter((a) => a.category === activeSlug);
  }, [activeSlug]);

  const pageTitle =
    activeSlug === "all"
      ? "全部评测 - 蘑菇视频平台对比评测"
      : `${activeCategory.name} - 蘑菇视频平台评测`;
  const pageDesc =
    activeSlug === "all"
      ? "浏览蘑菇视频全部视频平台对比评测文章，涵盖蘑菇视频、樱花视频、樱桃视频、糖豆视频等平台的资源、流畅度、画质等多维度对比。"
      : `${activeCategory.description}，蘑菇视频为您提供详细的视频平台${activeCategory.name}评测内容。`;
  const canonicalUrl = `https://mogutv.com/category/${activeSlug}`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`蘑菇视频,${activeCategory.name},视频平台,评测,对比`}
        canonicalUrl={canonicalUrl}
      />
      <WebPageSchema
        title={pageTitle}
        description={pageDesc}
        url={canonicalUrl}
        breadcrumbs={[
          { name: "首页", url: "https://mogutv.com" },
          { name: activeCategory.name, url: canonicalUrl },
        ]}
      />

      <main className="pt-20">
        {/* Header */}
        <section className="w-full bg-background-100/70 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 py-10">
            <div className="mb-4">
              <Breadcrumbs
                items={[
                  { label: "首页", href: "/" },
                  { label: activeCategory.name },
                ]}
              />
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              {activeCategory.name}
            </h1>
            <p className="text-sm text-foreground-600">{activeCategory.description}</p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full bg-background-50 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 py-3 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Article Grid */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            {filteredArticles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-sm text-foreground-600">该分类暂无评测文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}